import { Component, inject, HostListener, ElementRef, OnDestroy, computed } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Capacitor } from '@capacitor/core';
import { Store } from '@ngrx/store';
import {
  selectUserInfo,
  selectBranchId,
  selectDoctorInfo,
} from '../../../core/store/auth/auth.selectors';
import * as AuthActions from '../../../core/store/auth/auth.actions';
import { UIStateService, ActiveConsultation } from '../../../core/services/ui-state.service';
import { ConsultationService } from '../../../core/services/consultation.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, filter, distinctUntilChanged, switchMap, catchError, of } from 'rxjs';
import { toast } from 'ngx-sonner';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';

import { DialogService } from 'primeng/dynamicdialog';
import { RoutingAwareDialogService } from '../../../core/services/routing-aware-dialog.service';
import { UserProfileModalComponent } from '../user-profile-modal/user-profile-modal.component';
import { HeaderProfilePopup } from '../header-profile-popup/header-profile-popup';
import { DiagnocatTrackerService } from '../../../core/services/diagnocat-tracker.service';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { AppButton } from '../app-button/app-button';
import { NotificationService, AppNotification } from '../../../core/services/notification.service';
import { PushService } from '../../../core/services/push.service';

@Component({
  selector: 'app-dashboard-header',
  standalone: true,
  imports: [CommonModule, TranslateModule, TooltipModule, HeaderProfilePopup, ConfirmDialogModule, AppButton],
  providers: [{ provide: DialogService, useClass: RoutingAwareDialogService }, ConfirmationService],
  templateUrl: './dashboard-header.html',
  styleUrl: './dashboard-header.css'
})
export class DashboardHeader implements OnDestroy {
  private store = inject(Store);
  private uiState = inject(UIStateService);
  private consultationService = inject(ConsultationService);
  private translate = inject(TranslateService);
  private dialogService = inject(DialogService);
  private router = inject(Router);
  private el = inject(ElementRef);
  private diagnocatTracker = inject(DiagnocatTrackerService);
  private confirmationService = inject(ConfirmationService);
  private notificationService = inject(NotificationService);
  private pushService = inject(PushService);

  // Generic notification bell
  notifications$ = this.notificationService.entitled$;
  notificationCount$ = this.notificationService.count$;
  notificationReadIds$ = this.notificationService.readIds;
  showNotifMenu = false;

  // Browser push opt-in, shown inline in the notification dropdown.
  canEnablePush = computed(
    () => this.pushService.available && this.pushService.permission() === 'default',
  );
  pushBlocked = computed(
    () => this.pushService.available && this.pushService.permission() === 'denied',
  );
  pushBusy = this.pushService.busy;
  private foregroundUnsub: (() => void) | null = null;
  private pushPromptTimer: ReturnType<typeof setTimeout> | null = null;

  userInfo$ = this.store.select(selectUserInfo);
  isInConsultation$ = this.uiState.isInConsultation$;
  /** Every consultation this doctor has open — the badge shows a count and a
   *  list once there is more than one, so the state is honest from anywhere in
   *  the app rather than naming whichever happened to be opened last. */
  activeConsultations$ = this.uiState.activeConsultations$;
  /** Which of doctor/room actually differ between the open consultations, and
   *  so tell the reader something the patient name does not. */
  listMeta$ = this.uiState.activeConsultationsMeta$;
  showConsultationMenu = false;
  /** The caller's own doctors_tb id, or null for an admin/receptionist - the
   *  logout guard needs it to tell "my chair is still running" from "someone in
   *  this branch is with a patient". */
  private myDoctorId: number | null = null;

  goToConsultationById(patientId: number, appointmentId: number) {
    this.showConsultationMenu = false;
    this.router.navigate(['/consultations'], {
      queryParams: { patientID: patientId, appointmentID: appointmentId },
    });
  }
  showUserMenu = false;
  showLangMenu = false;
  currentLang = 'pt';

  // Supported languages. Native names (endonyms) + a short code badge — no emoji
  // flags, which don't render on Windows. The "(Brasil)" qualifier is what makes
  // European vs Brazilian Portuguese unambiguous.
  readonly languages = [
    { code: 'en', name: 'English', short: 'EN' },
    { code: 'pt', name: 'Português', short: 'PT' },
    { code: 'es', name: 'Español', short: 'ES' },
    { code: 'pt-BR', name: 'Português (Brasil)', short: 'BR' },
  ];

  private routerSubscription: Subscription;

  constructor() {
    this.currentLang = this.translate.currentLang || 'en';

    // Start the generic notification stream (branch-scoped, realtime).
    this.notificationService.start();

    // The open-consultation list has to be loaded here, not only on
    // /consultations: the badge and the logout guard read it from every page,
    // and before this a hard refresh on the Agenda left it empty - the badge
    // vanished and logout stopped being blocked while chairs were still
    // running. Keyed on the branch for the same reason the notification stream
    // is: it is the signal that auth has resolved. The RPC scopes the list to
    // the caller rather than to the branch, but a branch switch is still the
    // right moment to re-read it.
    this.store
      .select(selectBranchId)
      .pipe(
        filter((id) => id != null),
        distinctUntilChanged(),
        // Caught INSIDE the switchMap on purpose: an error reaching the outer
        // stream would kill the subscription, and the list would then never
        // reload again for the rest of the session.
        switchMap(() =>
          this.consultationService.getMyActiveConsultations().pipe(
            catchError((err) => {
              console.error('Could not load active consultations:', err);
              return of([] as ActiveConsultation[]);
            }),
          ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe((list) => this.uiState.setActiveConsultations(list));

    this.store
      .select(selectDoctorInfo)
      .pipe(takeUntilDestroyed())
      .subscribe((d: any) => (this.myDoctorId = d?.id ?? null));

    // Browser push: web-only, skipped entirely inside the native Capacitor app.
    if (!Capacitor.isNativePlatform()) {
      void this.pushService.syncIfGranted();
      this.pushPromptTimer = setTimeout(() => void this.pushService.maybeAutoPrompt(), 3000);
      void this.pushService
        .onForegroundMessage(() => this.notificationService.refresh())
        .then((unsub) => {
          this.foregroundUnsub = unsub;
        });
    }

    // Close menus on navigation
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.showUserMenu = false;
      this.showLangMenu = false;
      this.showNotifMenu = false;
    });
  }

  enablePush() {
    void this.pushService.enable();
  }

  toggleNotifMenu(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.showNotifMenu = !this.showNotifMenu;
    this.showUserMenu = false;
    this.showLangMenu = false;
  }

  openNotification(n: AppNotification) {
    this.showNotifMenu = false;
    void this.notificationService.markRead(n.id);
    if (n.action_route) {
      // navigateByUrl, NOT navigate([...]): action_route may carry a query
      // string (e.g. '/schedule?date=2026-07-28&appointmentID=36951'), and
      // navigate() treats each array element as a single URL *segment*, so it
      // would percent-encode the '?' and match no route at all.
      this.router.navigateByUrl(n.action_route);
    }
  }

  viewAllNotifications() {
    this.showNotifMenu = false;
    this.router.navigate(['/notifications']);
  }

  /**
   * Deep-links to the preferences tab on the notifications page.
   *
   * Not /settings: that route is gated on `Configurações`, so the personal
   * preference screen was unreachable for most clinicians. /notifications
   * carries only the parent authGuard.
   */
  openNotificationSettings() {
    this.showNotifMenu = false;
    this.router.navigate(['/notifications'], { queryParams: { tab: 'preferences' } });
  }

  notificationIcon(type: string): string {
    switch (type) {
      case 'appointment_request':
        return 'pi-calendar-plus';
      default:
        return 'pi-bell';
    }
  }

  get activeLanguage() {
    return this.languages.find(l => l.code === this.currentLang) ?? this.languages[0];
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    this.notificationService.stop();
    if (this.pushPromptTimer) {
      clearTimeout(this.pushPromptTimer);
    }
    this.foregroundUnsub?.();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    // If a menu is open and the click is outside the header component, close it
    if (
      (this.showUserMenu || this.showLangMenu || this.showNotifMenu) &&
      !this.el.nativeElement.contains(event.target)
    ) {
      this.showUserMenu = false;
      this.showLangMenu = false;
      this.showNotifMenu = false;
    }
  }

  toggleMobileMenu() {
    this.uiState.toggleMobileSidebar();
  }

  toggleUserMenu(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.showUserMenu = !this.showUserMenu;
    this.showLangMenu = false;
    this.showNotifMenu = false;
  }

  toggleLangMenu(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.showLangMenu = !this.showLangMenu;
    this.showUserMenu = false;
    this.showNotifMenu = false;
  }

  openUserProfile() {
    this.showUserMenu = false;
    this.dialogService.open(UserProfileModalComponent, {
      header: this.translate.instant('dashboard.profile.title'),
      width: '540px',
      contentStyle: { overflow: 'auto' },
      baseZIndex: 10000,
      maximizable: false,
      closable: true,
      modal: true
    });
  }

  /**
   * The mobile banner's target. Prefers the consultation already on screen, so
   * tapping it while inside one is a no-op rather than a jump to somebody
   * else's chair; otherwise it opens the first one that is running.
   */
  goToOpenConsultation(openList: ActiveConsultation[]) {
    const viewed = this.uiState.getConsultationStatus().appointment;
    if (viewed?.patient_id && viewed?.id) {
      this.goToConsultationById(viewed.patient_id, viewed.id);
      return;
    }
    const first = openList[0];
    if (first) this.goToConsultationById(first.patient_id, first.appointment_id);
  }

  logout() {
    // Any still-open consultation blocks logout, not just the one on screen:
    // getConsultationStatus().inProgress means "the appointment I am looking at
    // is live", which would let a doctor log out from the agenda with three
    // chairs still running.
    //
    // This used to test the caller's doctor_id, because an admin's list was the
    // whole branch and guarding on "any" would have barred reception from ever
    // logging out. The list is now scoped to the caller by the RPC (started_by,
    // or assigned to me), so every row IS the caller's - and the old doctor_id
    // test was actively wrong for an Owner, who has no doctor id and so could
    // have logged out mid-consultation.
    if (this.uiState.hasActiveConsultations()) {
      toast.warning(this.translate.instant('consultation.end_required_to_logout'));
      this.showUserMenu = false;
      return;
    }

    // Check for active Diagnocat Analysis
    const diagnocatStatus = this.diagnocatTracker.currentStatus;
    if (diagnocatStatus.isProcessing) {
      this.showUserMenu = false;
      this.confirmationService.confirm({
        header: this.translate.instant('ATTENTION'),
        message: this.translate.instant('DIAGNOCAT_LOGOUT_CONFIRMATION'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translate.instant('YES'),
        rejectLabel: this.translate.instant('NO'),
        accept: () => {
          void this.pushService.unregister();
          this.store.dispatch(AuthActions.signOut());
        }
      });
      return;
    }

    void this.pushService.unregister();
    this.store.dispatch(AuthActions.signOut());
    this.showUserMenu = false;
  }

  selectLanguage(code: string) {
    this.translate.use(code);
    this.currentLang = code;
    localStorage.setItem('language', code);
    this.showLangMenu = false;
  }

  getUserInitials(name: string | null): string {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }
}
